import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import QuoteForm from '@/components/QuoteForm';
import {
  Shield,
  Leaf,
  Clock,
  ThumbsUp,
  Award,
  Home as HomeIcon,
  Building2,
  Sparkles,
  Users,
  Calendar,
  DollarSign,
  MapPin,
  Phone,
  ArrowRight,
} from 'lucide-react';

const Home = () => {
  const trustBadges = [
    { icon: Shield, text: 'Police-Checked Staff' },
    { icon: Leaf, text: 'Eco-Friendly Products' },
    { icon: Award, text: 'Fully Insured' },
    { icon: Clock, text: 'On-Time Guarantee' },
    { icon: ThumbsUp, text: 'Satisfaction Guarantee' },
  ];

  const services = [
    {
      icon: HomeIcon,
      title: 'Residential Cleaning',
      description: 'Keep your home spotless with our professional residential cleaning services. We handle everything from regular maintenance to deep cleaning.',
      image: 'https://images.unsplash.com/photo-1653242370332-e332a8103763?auto=format&fit=crop&q=80',
    },
    {
      icon: Building2,
      title: 'Office & Commercial',
      description: 'Maintain a pristine workspace that impresses clients and boosts employee morale. Flexible scheduling to suit your business hours.',
      image: 'https://images.pexels.com/photos/3316924/pexels-photo-3316924.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Sparkles,
      title: 'Eco-Friendly Deep Cleaning',
      description: 'Our signature deep cleaning service uses only environmentally safe products. Perfect for allergy-sensitive homes and eco-conscious businesses.',
      image: 'https://images.pexels.com/photos/33869721/pexels-photo-33869721.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  const whyChooseUs = [
    { icon: Users, title: 'Trained Professionals', text: 'Our team is fully trained and background-checked' },
    { icon: Calendar, title: 'Flexible Scheduling', text: 'Book at times that work for you' },
    { icon: DollarSign, title: 'Transparent Pricing', text: 'No hidden fees, clear quotes upfront' },
    { icon: MapPin, title: 'Perth-Based', text: 'Local service you can trust' },
  ];

  const galleryImages = [
    'https://images.pexels.com/photos/10568358/pexels-photo-10568358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.pexels.com/photos/5591907/pexels-photo-5591907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.unsplash.com/photo-1597406462638-5700a53b53d8?auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1740694125619-8e72f30eaeee?auto=format&fit=crop&q=80',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section data-testid="hero-section" className="hero-section">
        <img
          src="https://images.pexels.com/photos/10568358/pexels-photo-10568358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Professional cleaning service"
          className="hero-bg"
        />
        <div className="hero-overlay" />
        <div className="hero-content container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
              BrightEdge Cleaning Perth
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-3">
              Professional Home & Office Cleaning
            </p>
            <p className="text-lg md:text-xl text-white/80 mb-8">
              Eco-Friendly & Reliable
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up">
              <Button
                asChild
                data-testid="hero-quote-btn"
                className="btn-primary text-lg"
              >
                <a href="#quote-form">Get a Free Quote</a>
              </Button>
              <Button
                asChild
                data-testid="hero-call-btn"
                className="btn-secondary text-lg"
              >
                <a href="tel:+61400000000">
                  <Phone className="mr-2" size={20} />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section data-testid="trust-badges" className="py-12 bg-secondary/30">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {trustBadges.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div
                  key={index}
                  data-testid={`trust-badge-${index}`}
                  className="trust-badge flex items-center space-x-2"
                >
                  <Icon size={16} className="text-primary" />
                  <span>{badge.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview" className="py-16 md:py-24 lg:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              Our Services
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Professional cleaning solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  data-testid={`service-card-${index}`}
                  className="service-card"
                >
                  <div className="mb-4 overflow-hidden rounded-xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <Icon className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-2xl font-semibold text-primary mb-3">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              data-testid="view-all-services-btn"
              className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6"
            >
              <Link to="/services">
                View All Services
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-us" className="py-16 md:py-24 bg-surface">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              Why Choose BrightEdge?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              We're committed to delivering exceptional cleaning services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  data-testid={`why-us-${index}`}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section data-testid="gallery-preview" className="py-16 md:py-24 bg-surface">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              Before & After
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              See the difference professional cleaning makes
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                data-testid={`gallery-image-${index}`}
                className="relative overflow-hidden rounded-xl aspect-square group cursor-pointer"
              >
                <img
                  src={image}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              data-testid="gallery-page-btn"
              className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 py-6"
            >
              <Link to="/gallery">
                View Full Gallery
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" data-testid="quote-form-section" className="py-16 md:py-24 lg:py-32 bg-gradient-to-br from-white to-secondary">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4">
              Get Your Free Quote
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Fill out the form below and we'll get back to you within 24 hours
            </p>
          </div>

          <QuoteForm />
        </div>
      </section>
    </div>
  );
};

export default Home;
